import { createBrowserClient } from '@supabase/ssr';
import { createClient as createSupabaseClient } from '@supabase/supabase-js';

const SUPABASE_URL = process.env.NEXT_PUBLIC_SUPABASE_URL!;
const SUPABASE_ANON_KEY = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY!;

// ─── AUTH CLIENT (singleton) ─────────────────────────────────────────────────
// Used ONLY for authentication operations.
// Uses localStorage exclusively — no cookies, no Web Locks API conflicts.
let _authClient: ReturnType<typeof createBrowserClient> | null = null;

export function createClient() {
  if (typeof window !== 'undefined' && _authClient) {
    return _authClient;
  }

  const client = createBrowserClient(
    SUPABASE_URL,
    SUPABASE_ANON_KEY,
    {
      auth: {
        autoRefreshToken: true,
        persistSession: true,
        detectSessionInUrl: false,
        storageKey: 'ibdaa-auth-token',
        storage: typeof window !== 'undefined' ? {
          getItem: (key: string) => window.localStorage.getItem(key),
          setItem: (key: string, value: string) => window.localStorage.setItem(key, value),
          removeItem: (key: string) => window.localStorage.removeItem(key),
        } : undefined,
      },
    }
  );

  if (typeof window !== 'undefined') {
    _authClient = client;
  }

  return client;
}

// ─── ANON DATA CLIENT (singleton) ────────────────────────────────────────────
// Uses plain @supabase/supabase-js (NOT @supabase/ssr).
// No Web Locks, no session persistence, no token refresh.
// Safe for all public data reads (categories, partners, etc.).
let _anonClient: ReturnType<typeof createSupabaseClient> | null = null;

export function createAnonClient() {
  if (typeof window !== 'undefined' && _anonClient) {
    return _anonClient;
  }

  const client = createSupabaseClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
    auth: {
      autoRefreshToken: false,
      persistSession: false,
      detectSessionInUrl: false,
    },
    global: {
      headers: { 'x-client-info': 'anon-data-client' },
    },
  });

  if (typeof window !== 'undefined') {
    _anonClient = client;
  }

  return client;
}

// ─── DIRECT REST FETCH ────────────────────────────────────────────────────────
// Zero-dependency public data fetcher — no Supabase client, no locks, no auth.
// Use for simple SELECT queries on public tables.
export async function supabaseFetch(
  table: string,
  params: Record<string, string> = {},
  select = '*'
): Promise<{ data: any[] | null; error: Error | null }> {
  try {
    const url = new URL(`${SUPABASE_URL}/rest/v1/${table}`);
    url.searchParams.set('select', select);
    Object.entries(params).forEach(([k, v]) => url.searchParams.set(k, v));

    const res = await fetch(url.toString(), {
      headers: {
        apikey: SUPABASE_ANON_KEY,
        Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
        'Content-Type': 'application/json',
      },
    });

    if (!res.ok) {
      const text = await res.text();
      return { data: null, error: new Error(text) };
    }

    const data = await res.json();
    return { data, error: null };
  } catch (err: any) {
    return { data: null, error: err };
  }
}
